/**
 * ProductPriceChart — visualisation historique de prix ROBUSTE et partagée
 * entre la landing page et la vraie fiche produit.
 *
 * Feb 2026 v2 — Fix « points collés en haut + tooltip du dernier point qui
 * déborde » (spec commit `chart-tooltip-boundaries`).
 *
 * Cause racine du placement en haut :
 *  Le wrapper (ref) n'avait pas de hauteur explicite, donc `h-full` passé
 *  par le parent résolvait à 0 → les points positionnés en `top: 86%` de
 *  0 px se retrouvaient tous à `top: 0` visuellement (juste sous les
 *  boutons de période). Correctif : la classe de hauteur est appliquée sur
 *  le CONTENEUR extérieur, le graphique lui-même prend `flex-1 min-h-0`
 *  → la chaîne CSS de hauteur reste continue et le bandeau mobile
 *  éventuel se réserve son espace via flex-col.
 *
 * Cas « prix identiques » (min == max) : ligne horizontale centrée à
 * y = 50 % — plus de division par ~0 qui crée des points aberrants.
 *
 * Tooltip : jamais au-dessus (aucun risque de recouvrement des filtres
 * ou de l'indicateur de variation en-dehors du chart). Le dernier point
 * ouvre à gauche, le premier à droite, les autres en-dessous.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

const DEEP_BLUE = "#0B1F33";
const ORANGE    = "#FF9F1C";
const RED       = "#EF5352";
const GRID      = "#E9EDF3";

export default function ProductPriceChart({
  points = [],
  heightClassName,
  valueSuffix = "€",
  stroke = DEEP_BLUE,
  size = "md",
  className = "",
  testId = "product-price-chart",
  alwaysShowLabels,
  onPointEdit,   // Feb 2026 §« clic point → édition ligne source »
                  // Callback appelé avec le point complet (incl. invoice_id
                  // + line_id) quand l'utilisateur veut éditer l'observation.
}) {
  const prefersReducedMotion = useReducedMotion();
  const [hoveredIndex, setHoveredIndex] = useState(null);   // souris
  const [selectedIndex, setSelectedIndex] = useState(null); // tap persistant
  const [isNarrow, setIsNarrow] = useState(false);
  const wrapRef = useRef(null);

  useLayoutEffect(() => {
    const mql = window.matchMedia("(max-width: 640px)");
    const on = () => setIsNarrow(mql.matches);
    on();
    mql.addEventListener?.("change", on) || mql.addListener?.(on);
    return () => (mql.removeEventListener?.("change", on) || mql.removeListener?.(on));
  }, []);

  useEffect(() => {
    if (selectedIndex == null) return;
    const outside = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setSelectedIndex(null);
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("touchstart", outside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("touchstart", outside);
    };
  }, [selectedIndex]);

  const containerHeight = heightClassName || "h-[220px] sm:h-[260px] lg:h-[300px]";
  const showAll = alwaysShowLabels ?? points.length <= 3;

  if (!points || points.length === 0) {
    return (
      <div
        className={`w-full max-w-full ${containerHeight} flex items-center justify-center text-[13px] text-[#5E6875] ${className}`}
        data-testid={`${testId}-empty`}
      >
        Aucun relevé de prix enregistré.
      </div>
    );
  }

  // ---------- 1 seul relevé ----------
  if (points.length === 1) {
    const only = points[0];
    const s = size === "sm" ? 12 : size === "lg" ? 18 : 14;
    const canEdit = !!(onPointEdit && only?.invoice_id);
    return (
      <motion.div
        initial={prefersReducedMotion ? false : { opacity: 0 }}
        animate={prefersReducedMotion ? undefined : { opacity: 1 }}
        transition={prefersReducedMotion ? undefined : { duration: 0.4 }}
        className={`relative w-full max-w-full ${containerHeight} ${className}`}
        data-testid={testId}
      >
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full block" aria-hidden>
          {[25, 50, 75].map((y) => (
            <line key={y} x1="0" y1={y} x2="100" y2={y} stroke={GRID} strokeWidth="0.4" vectorEffect="non-scaling-stroke" />
          ))}
        </svg>
        {/* Feb 2026 §« clic point unique aussi éditable » — Le point
            singleton est un vrai bouton (clavier + tactile) qui ouvre le
            même InvoiceLineEditDialog si une source est disponible. */}
        <div
          role={canEdit ? "button" : undefined}
          tabIndex={canEdit ? 0 : undefined}
          onClick={canEdit ? (e) => { e.stopPropagation(); onPointEdit(only); } : undefined}
          onKeyDown={canEdit ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPointEdit(only); } } : undefined}
          className={`absolute inset-0 flex flex-col items-center justify-center gap-2 ${canEdit ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF9F1C] rounded-lg" : ""}`}
          aria-label={canEdit ? `${only.label} : ${only.value.toFixed(2).replace(".", ",")} ${valueSuffix} · éditer la ligne source` : undefined}
        >
          <div
            className="rounded-full bg-white"
            style={{ width: s + 6, height: s + 6, border: `3px solid ${ORANGE}`, boxShadow: "0 4px 12px rgba(11,31,51,0.25)" }}
            data-testid={`${testId}-point-0`}
          />
          <div className="px-2.5 py-1 rounded-md bg-[#0B1F33] text-white text-[11.5px] font-bold tabular-nums shadow-md" data-testid={`${testId}-label-0`}>
            <span className="opacity-70 font-semibold">{only.label}</span>
            <span className="mx-1">·</span>
            <span>{only.value.toFixed(2).replace(".", ",")} {valueSuffix}</span>
          </div>
          <div className="text-[11.5px] text-[#5E6875] font-semibold" data-testid={`${testId}-single-hint`}>
            Premier relevé enregistré
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onPointEdit(only); }}
              data-testid={`${testId}-edit-0`}
              className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0B1F33] text-white text-[10px] font-bold uppercase tracking-widest shadow-md hover:bg-[#1a2f47]"
            >
              Éditer la ligne source
            </button>
          )}
        </div>
      </motion.div>
    );
  }

  // ---------- Échelle ----------
  //
  // Feb 2026 §Chart-scale — Le graphique doit refléter l'AMPLITUDE RÉELLE de
  // la variation, pas l'écraser sur toute la hauteur. Sans garde-fou, une
  // différence de 0,003 € entre deux points (ex 1,493 → 1,490 €/L) se traduit
  // par une chute pleine hauteur — visuellement trompeur.
  //
  // Règle unifiée pour tous les produits (aucun cas particulier par nom) :
  //   1. On calcule la fourchette brute (max - min).
  //   2. On impose une AMPLITUDE MINIMALE = 20 % de la médiane (ou du prix).
  //      → une variation < 20 % du prix se rend visuellement discrète,
  //        conformément à son importance économique réelle.
  //   3. On centre la fourchette autour du milieu (min ↔ max), puis on
  //      élargit à ±½ amplitude.
  //   4. On garde la marge PADY habituelle pour ne jamais coller aux bords.
  //
  // Cas plat (min ≈ max) : on force la ligne à y = 50 %.
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const rangeRaw = max - min;

  const sorted = [...values].sort((a, b) => a - b);
  const median = sorted.length % 2
    ? sorted[(sorted.length - 1) / 2]
    : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2;
  const anchor = median || max || 1;
  const MIN_AMPLITUDE_RATIO = 0.20; // 20 % du prix médian
  const minAmplitude = Math.abs(anchor) * MIN_AMPLITUDE_RATIO;

  const isFlat = rangeRaw < 1e-6;
  const effectiveRange = Math.max(rangeRaw, minAmplitude, 1e-6);
  const mid = (min + max) / 2;
  const yMin = mid - effectiveRange / 2;
  const yMax = mid + effectiveRange / 2;
  const yRange = Math.max(yMax - yMin, 1e-6);

  const PADX = 6, PADY = 14;
  const spot = (i) => {
    const x = PADX + (i / (points.length - 1)) * (100 - 2 * PADX);
    const y = isFlat
      ? 50
      : PADY + (1 - (points[i].value - yMin) / yRange) * (100 - 2 * PADY);
    return { x, y };
  };
  const spots = points.map((_, i) => spot(i));
  const polylinePts = spots.map((p) => `${p.x.toFixed(3)},${p.y.toFixed(3)}`).join(" ");

  // ---------- Précision adaptative des étiquettes ----------
  //
  // Feb 2026 §Precision — Si l'écart entre min et max est < 0,01 sur la
  // fourchette réelle, deux décimales masqueraient toute la variation
  // (1,493 → 1,490 s'affichent en « 1,49 » et « 1,49 »). On adapte alors
  // à 3 ou 4 décimales pour rendre l'évolution lisible.
  const decimals = rangeRaw >= 0.5 ? 2 : rangeRaw >= 0.05 ? 3 : rangeRaw >= 0.005 ? 3 : 4;
  const fmtValue = (v) => v.toFixed(decimals).replace(".", ",");

  const dotSize = size === "sm" ? 10 : size === "lg" ? 16 : 12;
  const lastDotSize = dotSize + 4;

  const initial = prefersReducedMotion ? false : { opacity: 0 };
  const animate = prefersReducedMotion ? undefined : { opacity: 1 };
  const transition = prefersReducedMotion ? undefined : { duration: 0.5, ease: [0.22, 0.61, 0.36, 1] };

  const activeIndex = selectedIndex != null ? selectedIndex : hoveredIndex;
  const useMobilePanel = isNarrow;

  const activatePoint = (i) => { setSelectedIndex(i); setHoveredIndex(i); };

  return (
    // Feb 2026 — Chaîne CSS de hauteur intacte : la hauteur voulue est
    // appliquée ICI, le graphique interne prend `flex-1 min-h-0`. Le
    // bandeau mobile (facultatif) se réserve son espace sans casser le
    // ratio du SVG.
    <div
      ref={wrapRef}
      className={`w-full max-w-full ${containerHeight} flex flex-col ${className}`}
      data-testid={testId}
    >
      <motion.div
        initial={initial}
        animate={animate}
        transition={transition}
        className="relative w-full max-w-full flex-1 min-h-0"
        style={{ overflow: "visible" }}
      >
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full block" aria-hidden>
          {[25, 50, 75].map((y) => (
            <line key={y} x1="0" y1={y} x2="100" y2={y} stroke={GRID} strokeWidth="0.4" vectorEffect="non-scaling-stroke" />
          ))}
          <polyline
            points={polylinePts}
            fill="none"
            stroke={stroke}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {/* Points en HTML overlay — cible tactile 40×40 px. */}
        {spots.map((p, i) => {
          const isLast = i === spots.length - 1;
          const s = isLast ? lastDotSize : dotSize;
          const isActive = activeIndex === i || showAll;
          const isSelected = selectedIndex === i;
          const canEdit = !!(onPointEdit && points[i]?.invoice_id);
          return (
            <div
              key={`pt-${i}`}
              role="button"
              tabIndex={0}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex((cur) => (cur === i ? null : cur))}
              onFocus={() => setHoveredIndex(i)}
              onBlur={() => setHoveredIndex((cur) => (cur === i ? null : cur))}
              onClick={(e) => {
                e.stopPropagation();
                // Feb 2026 §« double-clic point → édition » — un clic simple
                // active/sélectionne (comportement d'origine), un DOUBLE clic
                // OU un long-press ouvre l'éditeur de la ligne source. Un
                // clic simple ne mute JAMAIS de données.
                if (isSelected && canEdit) {
                  onPointEdit(points[i]);
                } else {
                  activatePoint(i);
                }
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (canEdit) onPointEdit(points[i]);
              }}
              onTouchStart={(e) => { e.stopPropagation(); activatePoint(i); }}
              className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center focus-visible:outline-none cursor-pointer"
              style={{ left: `${p.x}%`, top: `${p.y}%`, width: 40, height: 40, zIndex: 2 }}
              data-testid={`${testId}-point-${i}`}
              aria-label={`${points[i].label} : ${fmtValue(points[i].value)} ${valueSuffix}${canEdit ? " · double-clic pour éditer la ligne source" : ""}`}
              aria-pressed={isSelected}
            >
              <div
                className="rounded-full bg-white transition-transform duration-150"
                style={{
                  width: s, height: s,
                  border: `${isLast ? 3 : 2}px solid ${isLast ? RED : ORANGE}`,
                  boxShadow: isSelected
                    ? `0 0 0 4px ${isLast ? "rgba(239,83,82,0.25)" : "rgba(255,159,28,0.30)"}, 0 4px 12px rgba(11,31,51,0.28)`
                    : isActive
                    ? "0 4px 12px rgba(11,31,51,0.25)"
                    : "0 2px 6px rgba(11,31,51,0.18)",
                  transform: isSelected ? "scale(1.35)" : isActive ? "scale(1.15)" : "scale(1)",
                }}
              />
              {/* Bouton Éditer visible dès qu'un point est sélectionné (tap
                  ou clic simple) — pas de mutation au tap, l'utilisateur
                  doit explicitement cliquer sur ce bouton pour ouvrir
                  l'éditeur, puis Annuler/Enregistrer dans le dialog. */}
              {isSelected && canEdit && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onPointEdit(points[i]); }}
                  onTouchStart={(e) => { e.stopPropagation(); }}
                  data-testid={`${testId}-edit-${i}`}
                  className="absolute top-full mt-1 left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-0.5 rounded-full bg-[#0B1F33] text-white text-[10px] font-bold uppercase tracking-widest shadow-md hover:bg-[#1a2f47]"
                  style={{ zIndex: 3 }}
                >
                  Éditer
                </button>
              )}
            </div>
          );
        })}

        {/* Tooltips flottantes desktop UNIQUEMENT — jamais au-dessus du
            point (éviterait de recouvrir les filtres / l'indicateur de
            variation qui sont RENDUS EN DEHORS du composant, juste
            au-dessus du chart). */}
        {!useMobilePanel && points.map((pt, i) => {
          const p = spots[i];
          const visible = showAll || activeIndex === i;
          if (!visible) return null;
          const isLast = i === spots.length - 1;
          const isFirst = i === 0;
          // Feb 2026 — Placement anti-débordement :
          //  - dernier point → tooltip ANCRÉE au bord droit du chart
          //    (right: 4px) puis centrée verticalement sur le point. Aucune
          //    coupure possible par la bordure de la carte.
          //  - premier point → même logique, ancrée à gauche.
          //  - milieu → au-dessus (jamais recouvre les filtres car en dehors
          //    de la zone où sont les boutons de période).
          if (isLast) {
            return (
              <motion.div
                key={`tt-${i}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.15 }}
                className="absolute pointer-events-none"
                style={{
                  right: "4px",
                  top: `${Math.min(Math.max(p.y, 12), 88)}%`,
                  transform: "translateY(-50%)",
                  zIndex: 3,
                  maxWidth: "min(220px, calc(100% - 24px))",
                }}
                data-testid={`${testId}-label-${i}`}
              >
                <div className="px-2.5 py-1.5 rounded-md text-[11.5px] font-bold tabular-nums shadow-md leading-tight whitespace-nowrap bg-[#0B1F33] text-white">
                  <span className="opacity-70 font-semibold">{pt.label}</span>
                  <span className="mx-1">·</span>
                  <span>{fmtValue(pt.value)} {valueSuffix}</span>
                </div>
              </motion.div>
            );
          }
          if (isFirst) {
            return (
              <motion.div
                key={`tt-${i}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.15 }}
                className="absolute pointer-events-none"
                style={{
                  left: "4px",
                  top: `${Math.min(Math.max(p.y, 12), 88)}%`,
                  transform: "translateY(-50%)",
                  zIndex: 3,
                  maxWidth: "min(220px, calc(100% - 24px))",
                }}
                data-testid={`${testId}-label-${i}`}
              >
                <div className="px-2.5 py-1.5 rounded-md text-[11.5px] font-bold tabular-nums shadow-md leading-tight whitespace-nowrap bg-white text-[#0B1F33] border border-[#CDD7E3]">
                  <span className="opacity-70 font-semibold">{pt.label}</span>
                  <span className="mx-1">·</span>
                  <span>{fmtValue(pt.value)} {valueSuffix}</span>
                </div>
              </motion.div>
            );
          }
          // Points intermédiaires — en dessous, jamais au-dessus (évite
          // les filtres/indicateur rendus par le parent au-dessus du chart).
          return (
            <motion.div
              key={`tt-${i}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.15 }}
              className="absolute pointer-events-none"
              style={{
                left: `${Math.min(Math.max(p.x, 15), 85)}%`,
                top:  `${p.y}%`,
                transform: "translate(-50%, 14px)",
                zIndex: 3,
                maxWidth: "min(220px, calc(100% - 24px))",
              }}
              data-testid={`${testId}-label-${i}`}
            >
              <div className="px-2.5 py-1.5 rounded-md text-[11.5px] font-bold tabular-nums shadow-md leading-tight whitespace-nowrap bg-white text-[#0B1F33] border border-[#CDD7E3]">
                <span className="opacity-70 font-semibold">{pt.label}</span>
                <span className="mx-1">·</span>
                <span>{fmtValue(pt.value)} {valueSuffix}</span>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Bandeau fixe mobile — SOUS le graphique, jamais coupé. */}
      {useMobilePanel && (() => {
        const idx = activeIndex != null ? activeIndex : spots.length - 1;
        const pt = points[idx];
        const isLast = idx === spots.length - 1;
        return (
          <div
            className="mt-2 shrink-0 rounded-md border border-[#CDD7E3] bg-white px-3 py-2 flex items-center justify-between gap-3"
            data-testid={`${testId}-mobile-panel`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: isLast ? RED : ORANGE }} />
              <span className="text-[11.5px] uppercase tracking-widest font-bold text-[#5E6875]">
                {activeIndex != null ? "Point sélectionné" : "Dernier relevé"}
              </span>
            </div>
            <div className="text-right shrink-0" data-testid={`${testId}-mobile-panel-value`}>
              <div className="text-[11px] text-[#5E6875] font-semibold leading-tight">{pt.label}</div>
              <div className="text-[13.5px] font-mono-num font-extrabold text-[#0B1F33] tabular-nums leading-tight">
                {fmtValue(pt.value)} {valueSuffix}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
